import type { GameEvent, GameState } from '@shared/game/types';
import type { CharacterPreviewRig } from './preview-rig';
import type { EyeExpression } from './types';

export type EyeExpressionLayer =
  | 'idle'
  | 'movement'
  | 'action'
  | 'hit'
  | 'result'
  | 'status'
  | 'dead';

export interface CharacterEyeExpressionManager {
  showTimed(
    playerId: string,
    layer: EyeExpressionLayer,
    expression: EyeExpression,
    ms: number,
  ): void;
  setSticky(playerId: string, layer: EyeExpressionLayer, expression: EyeExpression): void;
  clearLayer(playerId: string, layer: EyeExpressionLayer): void;
  clearLayerForAll(layer: EyeExpressionLayer): void;
  refresh(playerId: string): void;
  cleanupPlayer(playerId: string): void;
  cleanupMissingPlayers(presentPlayerIds: ReadonlySet<string>): void;
  syncFromState(state: GameState): void;
  handleEvent(event: GameEvent): void;
  dispose(): void;
}

interface Config {
  readonly getRig: (playerId: string) => CharacterPreviewRig | null;
  readonly now?: () => number;
  readonly setTimer?: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  readonly clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
}

interface LayerRecord {
  expression: EyeExpression;
  expiresAt: number | null;
  timer: ReturnType<typeof setTimeout> | null;
}

const PRIORITY: Readonly<Record<EyeExpressionLayer, number>> = {
  dead: 100,
  status: 80,
  result: 70,
  hit: 60,
  action: 40,
  movement: 30,
  idle: 0,
};

const DEAD_MINIMUM_MS = 900;

export function createCharacterEyeExpressionManager(config: Config): CharacterEyeExpressionManager {
  const layersByPlayer = new Map<string, Map<EyeExpressionLayer, LayerRecord>>();
  const now = config.now ?? (() => Date.now());
  const setTimer = config.setTimer ?? ((callback, ms) => setTimeout(callback, ms));
  const clearTimer = config.clearTimer ?? ((handle) => clearTimeout(handle));
  let disposed = false;

  function recordsFor(playerId: string): Map<EyeExpressionLayer, LayerRecord> {
    let records = layersByPlayer.get(playerId);
    if (records == null) {
      records = new Map();
      layersByPlayer.set(playerId, records);
    }
    return records;
  }

  function apply(playerId: string): void {
    if (disposed) return;
    const rig = config.getRig(playerId);
    if (rig == null) return;
    const records = layersByPlayer.get(playerId);
    let expression: EyeExpression = 'base';
    let bestPriority = -1;
    if (records != null) {
      const currentTime = now();
      for (const [layer, record] of records) {
        if (record.expiresAt != null && record.expiresAt <= currentTime) continue;
        const priority = PRIORITY[layer];
        if (priority > bestPriority) {
          expression = record.expression;
          bestPriority = priority;
        }
      }
    }
    rig.setEyeExpression(expression);
  }

  function setRecord(
    playerId: string,
    layer: EyeExpressionLayer,
    expression: EyeExpression,
    ms: number | null,
  ): void {
    if (disposed) return;
    const records = recordsFor(playerId);
    const previous = records.get(layer);
    if (previous?.timer != null) clearTimer(previous.timer);
    const expiresAt = ms == null ? null : now() + ms;
    const record: LayerRecord = { expression, expiresAt, timer: null };
    if (ms != null) {
      record.timer = setTimer(() => {
        const current = layersByPlayer.get(playerId)?.get(layer);
        if (current !== record) return;
        clearLayer(playerId, layer);
      }, ms);
    }
    records.set(layer, record);
    apply(playerId);
  }

  function showTimed(
    playerId: string,
    layer: EyeExpressionLayer,
    expression: EyeExpression,
    ms: number,
  ): void {
    const duration = layer === 'dead' ? Math.max(ms, DEAD_MINIMUM_MS) : ms;
    setRecord(playerId, layer, expression, duration);
  }

  function setSticky(playerId: string, layer: EyeExpressionLayer, expression: EyeExpression): void {
    setRecord(playerId, layer, expression, null);
  }

  function clearLayer(playerId: string, layer: EyeExpressionLayer): void {
    const records = layersByPlayer.get(playerId);
    const record = records?.get(layer);
    if (record == null) return;
    if (record.timer != null) clearTimer(record.timer);
    records!.delete(layer);
    if (records!.size === 0) layersByPlayer.delete(playerId);
    apply(playerId);
  }

  function clearLayerForAll(layer: EyeExpressionLayer): void {
    for (const playerId of [...layersByPlayer.keys()]) {
      clearLayer(playerId, layer);
    }
  }

  function refresh(playerId: string): void {
    apply(playerId);
  }

  function cleanupPlayer(playerId: string): void {
    const records = layersByPlayer.get(playerId);
    if (records == null) return;
    for (const record of records.values()) {
      if (record.timer != null) clearTimer(record.timer);
    }
    layersByPlayer.delete(playerId);
  }

  function cleanupMissingPlayers(presentPlayerIds: ReadonlySet<string>): void {
    for (const playerId of [...layersByPlayer.keys()]) {
      if (!presentPlayerIds.has(playerId)) cleanupPlayer(playerId);
    }
  }

  function syncFromState(state: GameState): void {
    const present = new Set(state.players.keys());
    cleanupMissingPlayers(present);

    for (const [playerId, player] of state.players) {
      if (player.isDead) setSticky(playerId, 'dead', 'dead');
      else clearLayer(playerId, 'dead');

      if (
        player.statusEffects.frozenUntil != null &&
        player.statusEffects.frozenUntil >= state.round
      ) {
        setSticky(playerId, 'status', 'confused');
      } else if (player.cursedRelicTurnsRemaining > 0) {
        setSticky(playerId, 'status', 'sad');
      } else {
        clearLayer(playerId, 'status');
      }
    }

    if (state.phase === 'game_over' && state.endGame != null) {
      const winnerId = state.endGame.winnerPlayerId;
      for (const playerId of present) {
        const player = state.players.get(playerId);
        if (player?.isDead) continue;
        setSticky(playerId, 'result', playerId === winnerId ? 'excited' : 'sad');
      }
    }
  }

  function handleEvent(event: GameEvent): void {
    switch (event.type) {
      case 'dice_roll':
        showTimed(event.playerId, 'action', 'excited', 1200);
        return;
      case 'movement_step':
        setSticky(event.playerId, 'movement', 'excited');
        return;
      case 'landing':
      case 'turn_end':
        clearLayer(event.playerId, 'movement');
        return;
      case 'damage':
      case 'item_hit':
      case 'item_dash_hit':
      case 'item_dot_tick':
      case 'npc_killed_player': {
        const playerId = targetPlayerForHitEvent(event);
        if (playerId !== '') showTimed(playerId, 'hit', 'angry', 700);
        return;
      }
      case 'heal':
      case 'player_received_item':
      case 'economy_change':
      case 'board_object_collected':
      case 'soul_anchor_claim':
      case 'award_granted':
        showTimed(event.playerId, 'action', 'happy', 900);
        return;
      case 'soul_anchor_trigger':
        showTimed(event.ownerPlayerId, 'action', 'happy', 900);
        showTimed(event.playerId, 'hit', event.effectType === 'hp' ? 'angry' : 'sad', 900);
        return;
      case 'item_cast':
      case 'item_dash':
      case 'item_beam_fired':
      case 'item_charge':
      case 'mystery_box_outcome':
      case 'mystery_box_choice_required':
      case 'mystery_box_choice_resolved':
      case 'npc_summoned':
        showTimed(event.playerId, 'action', 'excited', 900);
        return;
      case 'item_pull':
        showTimed(event.playerId, 'action', 'happy', 900);
        showTimed(event.victimId, 'hit', 'surprised', 900);
        return;
      case 'item_steal_keys':
      case 'item_steal_item':
        showTimed(event.playerId, 'action', 'happy', 900);
        showTimed(event.victimId, 'hit', 'sad', 900);
        return;
      case 'item_swap_positions':
        showTimed(event.playerId, 'action', 'surprised', 900);
        showTimed(event.targetId, 'hit', 'surprised', 900);
        return;
      case 'mystery_box_teleport':
        showTimed(event.playerId, 'action', 'surprised', 900);
        return;
      case 'item_missed':
      case 'mystery_box_trapped':
        showTimed(event.playerId, 'action', 'confused', 900);
        return;
      case 'item_blocked_stoneskin':
        showTimed(event.playerId, 'action', 'confused', 900);
        showTimed(event.blockerId, 'status', 'angry', 900);
        return;
      case 'death':
        showTimed(event.playerId, 'dead', 'dead', DEAD_MINIMUM_MS);
        return;
      case 'respawn':
        clearLayer(event.playerId, 'dead');
        showTimed(event.playerId, 'action', 'surprised', 900);
        return;
      case 'status_effect_applied':
        setSticky(
          event.playerId,
          'status',
          event.effectType === 'stoneSkin' ? 'angry' : 'confused',
        );
        return;
      case 'status_effect_expired':
        clearLayer(event.playerId, 'status');
        return;
      case 'item_dot_applied':
        setSticky(event.victimId, 'status', 'sad');
        return;
      case 'item_dot_expired':
        clearLayer(event.victimId, 'status');
        return;
      case 'soul_duel_triggered':
        showTimed(event.challengerId, 'action', 'angry', 1200);
        showTimed(event.defenderId, 'hit', 'surprised', 1200);
        return;
      case 'soul_duel_resolved':
        showTimed(event.winnerId, 'result', 'excited', 1600);
        showTimed(event.loserId, 'result', 'sad', 1600);
        return;
      case 'forced_relocation':
        showTimed(event.playerId, 'action', 'surprised', 900);
        return;
      case 'stone_skin_discard_prompt':
        showTimed(event.playerId, 'action', 'confused', 900);
        return;
      case 'npc_spawned':
        if (event.playerId != null) showTimed(event.playerId, 'action', 'excited', 900);
        return;
      default:
        return;
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const playerId of [...layersByPlayer.keys()]) cleanupPlayer(playerId);
  }

  return {
    showTimed,
    setSticky,
    clearLayer,
    clearLayerForAll,
    refresh,
    cleanupPlayer,
    cleanupMissingPlayers,
    syncFromState,
    handleEvent,
    dispose,
  };
}

function targetPlayerForHitEvent(event: GameEvent): string {
  switch (event.type) {
    case 'item_hit':
      return event.targetId;
    case 'item_dash_hit':
      return event.victimId;
    case 'item_dot_tick':
      return event.victimId;
    case 'npc_killed_player':
      return event.playerId;
    case 'damage':
      return event.playerId;
    default:
      return event.playerId ?? '';
  }
}
